import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AssistantConversationDTO, AssistantObjectRef, ConfirmTreeCard } from '@contracts/assistant';
import { ApiClientError, offlineError } from '../../api/client';
import { notify } from '@/lib/toast';
import { useAssistant, useAssistantOptional } from './AssistantProvider';
import { conversationKey, dropPending, editAction, useConversation } from './api';
import { draftOf, withDescendants, type S1Draft } from './draft';
import { refreshFor } from './refresh';
import { useTurn } from './useTurn';

export { DRAFT_MAP_HREF, draftOf, type DraftPending, type S1Draft } from './draft';

/** Every refusal about the draft is one toast, the next replacing it. */
const TOAST = 'hana-draft';
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';

/**
 * A narrowed draft's actionId → the actionId its draft started under, per query client (so per app, and per test).
 * Written by a ✕ that succeeds, before the narrowed pending reaches the cache.
 */
const lineages = new WeakMap<QueryClient, Map<string, string>>();
function lineageOf(queryClient: QueryClient): Map<string, string> {
  let lineage = lineages.get(queryClient);
  if (lineage === undefined) { lineage = new Map(); lineages.set(queryClient, lineage); }
  return lineage;
}

/**
 * The viewer's own latest draft (D206): the last pending `work.apply_plan` or heavy `work.move` of the conversation
 * this tab holds. Only the asker's conversation is ever read, so a colleague never sees another person's ghosts. Null
 * where Hana is not usable, and on a page without the assistant.
 */
export function usePendingDraft(): S1Draft | null {
  const ui = useAssistantOptional();
  const queryClient = useQueryClient();
  const { data } = useConversation(ui?.conversationId ?? null);
  const pending = ui === null ? undefined : data?.pending;
  // One draft object for as long as the conversation's pending list is the same: the Map lays out, measures and
  // focuses off it, so a new object each render would redraw every card on each pan frame (P4 G7 review 1).
  return useMemo(() => {
    if (pending === undefined) return null;
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      const draft = draftOf(pending[i]!);
      if (draft !== null) return { ...draft, line: lineageOf(queryClient).get(draft.line) ?? draft.line };
    }
    return null;
  }, [pending, queryClient]);
}

/** A stored `mission.create` input's steps, each named by its `ref` (instructions included, never shown). */
export const storedSteps = (input: Record<string, unknown>): { ref: string; title: string }[] =>
  (Array.isArray(input.steps) ? input.steps : []) as { ref: string; title: string }[];

export type DraftActions = {
  /** ✕ on a ghost: the same draft less that card and every card below it, through the edit endpoint (D206). */
  remove: (key: string) => void;
  confirm: () => void;
  reject: () => void;
  /** "Sửa trong chat": the panel, with the composer focused. */
  chat: () => void;
  /** "Xem kế hoạch" (a mission, mockup §1): the panel, with the plan's card brought into view. */
  show: () => void;
  busy: boolean;
};

/**
 * What the floating bar and a ghost's ✕ do (D206), through the same endpoints as the chat's confirm card: `edit`,
 * `confirm` and `reject`. A confirm or a reject takes the card off the conversation at once, so the ghosts go while
 * the resumed turn streams; a refusal (ACTION_STALE, PLAN_STALE…) is a toast, and the conversation read after it
 * brings the card back. The Map keeps it mounted for as long as Hana is usable there, not only while a draft is drawn: a
 * confirm takes the ghosts away, and the resumed turn it streams (the objects to glow, the undo to offer) is still read.
 */
export function useDraftActions(draft: S1Draft | null): DraftActions {
  const { conversationId, setOpen, glow, hanaUndo } = useAssistant();
  const queryClient = useQueryClient();
  const touched = useRef<AssistantObjectRef[]>([]);
  const turn = useTurn({
    onObjects: (objects, ok) => {
      touched.current = objects;
      if (ok && objects.length > 0) void refreshFor(queryClient, objects).then(() => glow(objects));
    },
    onUndo: (u) => hanaUndo.offer(u, touched.current),
    onRefused: (refused) => notify.error(refused.message || FAILED, { id: TOAST }),
  });
  const [editing, setEditing] = useState(false);
  const busy = editing || turn.running;

  const actionId = draft?.pending.actionId ?? null;
  const setPending = useCallback((next: (c: AssistantConversationDTO) => AssistantConversationDTO) => {
    if (conversationId === null) return;
    queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId),
      (c) => (c === undefined ? c : next(c)));
  }, [conversationId, queryClient]);

  const act = useCallback((kind: 'confirm' | 'reject') => {
    if (busy || actionId === null) return;
    dropPending(queryClient, conversationId, actionId);
    void turn.act(actionId, kind);
  }, [busy, queryClient, conversationId, turn, actionId]);

  const remove = useCallback((key: string) => {
    if (busy || draft === null || actionId === null) return;
    if (draft.kind === 'move') { act('reject'); return; }
    const gone = withDescendants(draft.cards, key);
    const cards: ConfirmTreeCard[] = draft.cards.filter((c) => !gone.has(c.key));
    // Nothing left to make (a mission without its goal or its last step): the draft itself goes.
    if (cards.length === 0 || (draft.mission && cards.length < 2)) { act('reject'); return; }
    // D116: a mission is narrowed by its steps; the rest of its input (`parentId`, `mission`…) is echoed as stored.
    const input = draft.mission
      ? { ...draft.pending.input, steps: storedSteps(draft.pending.input).filter((s) => !gone.has(s.ref)) }
      : { ...draft.pending.input, cards };
    setEditing(true);
    void (async () => {
      try {
        const next = await editAction(actionId, { input }, crypto.randomUUID());
        if (next.actionId !== actionId) lineageOf(queryClient).set(next.actionId, draft.line);
        setPending((c) => ({ ...c, pending: c.pending.map((p) => (p.actionId === actionId ? next : p)) }));
      } catch (e) {
        const err = e instanceof ApiClientError ? e : offlineError();
        const field = err.fields === undefined ? undefined : Object.values(err.fields)[0];
        notify.error(field ?? (err.message || FAILED), { id: TOAST });
      } finally {
        setEditing(false);
      }
    })();
  }, [busy, draft, act, actionId, setPending, queryClient]);

  const chat = useCallback(() => {
    setOpen(true);
    // The panel mounts on the next render (the phone sheet a moment later): the composer is focused once it is there.
    const focus = (tries: number) => {
      const box = document.querySelector<HTMLTextAreaElement>('[data-assistant-panel] textarea');
      if (box !== null) box.focus();
      else if (tries > 0) window.setTimeout(() => focus(tries - 1), 50);
    };
    window.setTimeout(() => focus(10), 0);
  }, [setOpen]);

  const show = useCallback(() => {
    setOpen(true);
    const reveal = (tries: number) => {
      const card = actionId === null ? null
        : document.querySelector<HTMLElement>(`[data-assistant-panel] [data-action="${CSS.escape(actionId)}"]`);
      if (card !== null) card.scrollIntoView?.({ block: 'nearest' });
      else if (tries > 0) window.setTimeout(() => reveal(tries - 1), 50);
    };
    window.setTimeout(() => reveal(10), 0);
  }, [setOpen, actionId]);

  return {
    remove, chat, show, busy,
    confirm: useCallback(() => act('confirm'), [act]),
    reject: useCallback(() => act('reject'), [act]),
  };
}

